import { areExpressionsInverses, CLASS_UOM_CONVERSION } from "c8-platform";
import { Context } from "csi/def/Context";
import { Field } from "csi/def/Field";
import { View } from "csi/def/View";
import Widget, { ErrorObject } from "csi/form/Widget";
import locale from "csi/locale";
import csi from "csi/main";
import sessionConstants from "csi/sessionConstants";
import { cnl } from "csi/typings";
import { DECIMAL_INTERNAL } from "csi/utility/numberUtility";
import xml from "csi/xml";
import Search from "csi/xml/Search";
import declare from "dojo/_base/declare";

const CONVERSION_TYPE = "ConversionType";
const EXTERNAL_UOM = "ExternalUOM";
const EXT_TO_INT_EXPRESSION = "ExtToIntExpression";
const INTERNAL_UOM = "InternalUOM";
const INT_TO_EXT_EXPRESSION = "IntToExtExpression";
const OPERATIONS = "[-+*\/]";

const UOMConversionForm = declare(Widget, {

	/** @property {boolean} bDefaultSaveAndNew - Do not use default SaveAndNew behavior. Overriden in resetForm() */
	bDefaultSaveAndNew: false,

	/** @property {any} cache - Cache for querying UnitOfMeasures and UOMConversions */
	cache: null,

	/** @property {Field} _conversionTypeField - Field for ConversionType */
	_conversionTypeField: null,

	/** @property {Field} _internalUOMField - Field for InternalUOM */
	_internalUOMField: null,

	/** @property {Field} _externalUOMField - Field for ExternalUOM */
	_externalUOMField: null,

	/** @property {Field} _extToIntExpressionField - Field for ExtToIntExpression */
	_extToIntExpressionField: null,

	/** @property {Field} _intToExtExpressionField - Field for IntToExtExpression */
	_intToExtExpressionField: null,
	
	/**
	 * @override
	 * Manage onChange logic and field disabling/enabling
	 */
	_resolveView(): void {
		this.inherited(arguments);

		this.cache = this.getCache() as any;
		this._conversionTypeField = this.getField(CONVERSION_TYPE);
		this._internalUOMField = this.getField(INTERNAL_UOM);
		this._externalUOMField = this.getField(EXTERNAL_UOM);
		this._extToIntExpressionField = this.getField(EXT_TO_INT_EXPRESSION);
		this._intToExtExpressionField = this.getField(INT_TO_EXT_EXPRESSION);

		if (this.isNew()) {
			this._conversionTypeField.m_onChange = "_onChangeUOMType";

			this._internalUOMField.m_onChange = "_onChangeUOM";
			this._externalUOMField.m_onChange = "_onChangeUOM";

			this._internalUOMField.m_onValidate = "_onValidateUOM";
			this._externalUOMField.m_onValidate = "_onValidateUOM";

			this.setUOMFieldsDisabled(true);
			this.disableExpressionFields();
		}
	},

	/**
	 * @override
	 * Add back locale decimal separator to expression fields
	 */
	_buildField(ctx: Context, field: Field, tr: HTMLElement, tdControl: HTMLElement) {
		this.inherited(arguments);
		if (!this.isEdit() || !(field.m_id === EXT_TO_INT_EXPRESSION || field.m_id === INT_TO_EXT_EXPRESSION)) {
			return;
		}
		this.setFieldValue(field.m_id, this.getFieldValue(field).replaceAll(DECIMAL_INTERNAL, sessionConstants.decimalSeparator));
	},

	_onChangeUOMType(defView: View, field: Field, value: cnl): void {
		this.setUOMFieldsDisabled(false);
		this.disableExpressionFields();

		// Reset disabled fields if UOMType changes
		this.setFieldValue(INTERNAL_UOM, csi.EMPTY_URL);
		this.setFieldValue(EXTERNAL_UOM, csi.EMPTY_URL);
		this.resetExpressions();

		if (field.getValue() === csi.EMPTY_URL) {
			this.setUOMFieldsDisabled(true);
		}
	},

	async _onChangeUOM(defView: View, field: Field, value: cnl): Promise<void> {
		const internalUOM = this._internalUOMField.getValue();
		const externalUOM = this._externalUOMField.getValue();

		if (internalUOM !== csi.EMPTY_URL && externalUOM !== csi.EMPTY_URL && internalUOM !== externalUOM && !this.isUOMConversionInCache()) {
			// Even though the UOMConversion is not in the cache, it may already exist so we need to search for it and cache it
			const search = new Search().byType(CLASS_UOM_CONVERSION);
			const query = search.getQuery();
			query.RefAttr(INTERNAL_UOM, xml.OP_EQUALS, internalUOM);
			query.RefAttr(EXTERNAL_UOM, xml.OP_EQUALS, externalUOM);
			const response = await search.getRequest()
				.go();
			this.cache.cacheResponse(response);
		}

		this.resetExpressions();
		this.validateUOMFields();
	},

	_onValidateUOM(defView: View, field: Field, value: cnl): void {
		if (value === csi.EMPTY_URL) {
			throw new Error(locale.resource("UOMConversionCreateEmptyUOM"));
		}

		const internalUOM = this._internalUOMField.getValue();
		const externalUOM = this._externalUOMField.getValue();

		if (internalUOM === csi.EMPTY_URL || externalUOM === csi.EMPTY_URL) {
			this.disableExpressionFields();
			return;
		}

		this.validateUOMPair(internalUOM, externalUOM);
	},

	/**
	 * Validate InternalUOM and ExternalUOM fields to see if they are the same or if the entry already exists.
	 * If the selected UOM is not in the cache yet, it will be loaded in by the framework
	 * before the _onChange event, where validation is forced for both fields again.
	 */
	validateUOMPair(internalUOM: cnl, externalUOM: cnl): void {
		if (internalUOM === externalUOM) {
			// Error for selecting same UOMs
			this.disableExpressionFields();
			throw new Error(locale.resource("UOMConversionCreateSameUOM"));
		}

		const internalUOMName = this.cache.getNode(internalUOM)?.$Name;
		const externalUOMName = this.cache.getNode(externalUOM)?.$Name;
		if (!internalUOMName || !externalUOMName) {
			// Selected UnitOfMeasure not loaded in cache yet
			return;
		}

		if (this.isUOMConversionInCache()) {
			// Error for trying to create existing UOMConversion
			this.disableExpressionFields();
			throw new Error(locale.resource("UOMConversionCreateAlreadyExists"));
		}

		this.enableExpressionFields();
	},

	setUOMFieldsDisabled(setDisabled: boolean): void {
		this.setFieldDisabled(INTERNAL_UOM, setDisabled);
		this.setFieldDisabled(EXTERNAL_UOM, setDisabled);
	},

	disableExpressionFields(): void {
		this.setFieldDisabled(EXT_TO_INT_EXPRESSION, true);
		this.setFieldDisabled(INT_TO_EXT_EXPRESSION, true);
	},

	enableExpressionFields(): void {
		this.setFieldDisabled(EXT_TO_INT_EXPRESSION, false);
		this.setFieldDisabled(INT_TO_EXT_EXPRESSION, false);
	},

	resetExpressions(): void {
		this.setFieldValue(EXT_TO_INT_EXPRESSION, "x * 1");
		this.setFieldValue(INT_TO_EXT_EXPRESSION, "x * 1");
	},

	/**
	 * This forces validation of the UOM fields
	 */
	validateUOMFields(): void {
		this.setFieldValue(INTERNAL_UOM, this._internalUOMField.getValue());
		this.setFieldValue(EXTERNAL_UOM, this._externalUOMField.getValue());
	},

	/**
	 * @override
	 * Check if expression is valid and flippable
	 */
	validateForm(errorParams: ErrorObject): boolean {
		this.formatExpressionFields();

		const extToIntExpression = this._extToIntExpressionField.getValue();
		const intToExtExpression = this._intToExtExpressionField.getValue();
		const extToIntExpressionFieldName = this._extToIntExpressionField.m_label;
		const intToExtExpressionFieldName = this._intToExtExpressionField.m_label;

		if (!extToIntExpression.includes("x")) {
			errorParams.message = locale.resource("UOMConversionExpressionNoVariable", [extToIntExpressionFieldName]);
			return false;
		} else if (!intToExtExpression.includes("x")) {
			errorParams.message = locale.resource("UOMConversionExpressionNoVariable", [intToExtExpressionFieldName]);
			return false;
		} else if (!extToIntExpression.match(OPERATIONS)) {
			errorParams.message = locale.resource("UOMConversionExpressionNoOperation", [extToIntExpressionFieldName]);
			return false;
		} else if (!intToExtExpression.match(OPERATIONS)) {
			errorParams.message = locale.resource("UOMConversionExpressionNoOperation", [intToExtExpressionFieldName]);
			return false;
		} else if (!areExpressionsInverses(extToIntExpression, intToExtExpression)) {
			errorParams.message = locale.resource("UOMConversionExpressionsInvalid");
			return false;
		}

		return this.inherited<boolean>(arguments);
	},

	/**
	 * @override
	 * SaveAndNew behavior should only populate UOMType field to what was selected before SaveAndNew action
	 */
	resetForm(): void {
		this._persistFormValues = {};
		const key = this._conversionTypeField.getUniqueControlName();
		this._persistFormValues[key] = this.getFieldValue(this._conversionTypeField);
		this.redraw();
	},

	/**
	 * Format expression fields to use default decimal separator
	 */
	formatExpressionFields(): void {
		const extToIntExp = this._extToIntExpressionField.getValue()
			.replaceAll(sessionConstants.decimalSeparator, DECIMAL_INTERNAL)
			.replaceAll(sessionConstants.groupSeparator, csi.EMPTY_STRING);
		const intToExtExp = this._intToExtExpressionField.getValue()
			.replaceAll(sessionConstants.decimalSeparator, DECIMAL_INTERNAL)
			.replaceAll(sessionConstants.groupSeparator, csi.EMPTY_STRING);
		this.setFieldValue(EXT_TO_INT_EXPRESSION, extToIntExp);
		this.setFieldValue(INT_TO_EXT_EXPRESSION, intToExtExp);
	},

	/**
	 * Does UOMConversion already exist in the cache
	 */
	isUOMConversionInCache(): boolean {
		const internalUOM = this._internalUOMField.getValue();
		const externalUOM = this._externalUOMField.getValue();
		return this.cache.query({ $Type: CLASS_UOM_CONVERSION, InternalUOM: internalUOM, ExternalUOM: externalUOM })
			// Checking that it’s not the client side Form node
			.find((conv) => !conv.$URL.includes("Form"));
	},
});

export = UOMConversionForm;